import { useState, useEffect, useRef } from 'react'
import { ChevronDown, Printer, Users, RefreshCw, Download } from 'lucide-react'
import leiloesService from '../../services/leiloesService'
import mapasService from '../../services/mapasService'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import { useAuthStore } from '../../stores/authStore'
import { downloadPdf } from '../../utils/pdfUtils'
import DateRangePicker from '../../components/ui/DateRangePicker'

// Período e Excel: só administradores e financeiro (Gestão Financeira) — o backend também exige
function podeExportar(user) {
  const nivel = user?.permission_level
  if (nivel === 'super_admin' || nivel === 'admin') return true
  if (nivel !== 'personalizado') return false
  return (user?.modulos_acesso || []).some(m => {
    const id = typeof m === 'string' ? m : m?.modulo
    return id === 'gestao' || String(id || '').startsWith('gestao-')
  })
}

// Datas pelo calendário local (toISOString usa UTC e adianta o dia depois das 21h no Brasil)
const isoLocal = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
const primeiroDiaMes = () => { const d = new Date(); return isoLocal(new Date(d.getFullYear(), d.getMonth(), 1)) }
const hoje = () => isoLocal(new Date())
const fmtDateBR = s => { if (!s) return ''; const [y, m, d] = s.split('-'); return `${d}/${m}/${y}` }

const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:8001').replace(/\/$/, '')
const brl = v => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)
const fmtDate = s => { if (!s) return ''; try { return new Date(s).toLocaleDateString('pt-BR') } catch { return s } }

function Select({ label, value, onChange, children, disabled, className = '' }) {
  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      {label && <label className="text-xs text-gray-500 font-medium">{label}</label>}
      <div className="relative">
        <select value={value} onChange={e => onChange(e.target.value)} disabled={disabled}
          className="w-full appearance-none border border-gray-300 rounded-lg px-3 py-2 pr-8 text-sm bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400">
          {children}
        </select>
        <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
      </div>
    </div>
  )
}

const S = {
  page: { fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 14, color: '#000', background: '#fff', padding: '20px 28px 24px' },
  hdr: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '2px solid #1f2937', paddingBottom: 14, marginBottom: 18 },
  hdrLogo: { maxHeight: 80, maxWidth: 180, objectFit: 'contain' },
  hdrCenter: { flex: 1, textAlign: 'center', padding: '0 16px' },
  hdrT1: { fontSize: 18, fontWeight: 700, textTransform: 'uppercase' },
  hdrT2: { fontSize: 13, color: '#374151', marginTop: 3 },
  hdrT3: { fontSize: 13, color: '#1f2937', marginTop: 2, fontWeight: 600 },
  secTitle: { fontSize: 14, fontWeight: 700, color: '#1f2937', marginTop: 16, marginBottom: 8 },
  tbl: { width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 8 },
  th: { borderBottom: '2px solid #d1d5db', padding: '8px 10px', textAlign: 'left', fontWeight: 700, fontSize: 12 },
  thR: { borderBottom: '2px solid #d1d5db', padding: '8px 10px', textAlign: 'right', fontWeight: 700, fontSize: 12 },
  thC: { borderBottom: '2px solid #d1d5db', padding: '8px 10px', textAlign: 'center', fontWeight: 700, fontSize: 12 },
  td: { borderBottom: '1px solid #e5e7eb', padding: '8px 10px', verticalAlign: 'middle' },
  tdR: { borderBottom: '1px solid #e5e7eb', padding: '8px 10px', textAlign: 'right', verticalAlign: 'middle' },
  tdC: { borderBottom: '1px solid #e5e7eb', padding: '8px 10px', textAlign: 'center', verticalAlign: 'middle' },
  totRow: { borderTop: '2px solid #f59e0b', fontWeight: 700 },
  footer: { textAlign: 'left', fontSize: 11, color: '#6b7280', marginTop: 20, paddingTop: 10, borderTop: '1px solid #e5e7eb' },
}

// ─── Fluxo 1: Comissão na Primeira Parcela ───────────────────────────────────
function TabelaFluxo1({ dados }) {
  const total = dados.clientes.reduce((s, c) => s + c.valor_comissao, 0)
  return (
    <>
      <table style={S.tbl}>
        <thead><tr><th style={S.th}>Cliente</th><th style={S.thR}>Valor Comissão</th></tr></thead>
        <tbody>
          {dados.clientes.map((c, i) => (
            <tr key={i}><td style={S.td}>{c.nome}</td><td style={S.tdR}>{brl(c.valor_comissao)}</td></tr>
          ))}
          <tr style={S.totRow}><td style={{ ...S.td, borderTop: '2px solid #f59e0b' }}><strong>TOTAL</strong></td><td style={{ ...S.tdR, borderTop: '2px solid #f59e0b' }}><strong>{brl(total)}</strong></td></tr>
        </tbody>
      </table>
    </>
  )
}

// ─── Fluxo 1 e 2: Comissão + Despesas ────────────────────────────────────────
function TabelaFluxo2({ dados }) {
  const labelDesp = dados.fluxo === 'gta_nota' ? 'Despesas (GTA + Nota)' : dados.fluxo === 'periodo' ? 'Despesas' : 'Despesas Lote'
  const totComissao = dados.clientes.reduce((s, c) => s + c.valor_comissao, 0)
  const totDespesas = dados.clientes.reduce((s, c) => s + c.despesas, 0)
  const totSubtotal = dados.clientes.reduce((s, c) => s + c.subtotal, 0)
  // Coluna Vencimento só aparece quando algum comprador teve o prazo da comissão definido
  const comVenc = dados.clientes.some(c => c.vencimento)
  const fmtVenc = iso => { const [a, m, d] = String(iso).slice(0, 10).split('-'); return `${d}/${m}/${a}` }
  const rotulo = dias => (dias <= 0 ? 'à vista' : `${dias} dia${dias === 1 ? '' : 's'}`)
  return (
    <table style={S.tbl}>
      <thead><tr>
        <th style={S.th}>Cliente</th>
        <th style={S.thR}>Valor Comissão</th>
        <th style={S.thR}>{labelDesp}</th>
        <th style={S.thR}>Total</th>
        {comVenc && <th style={S.thR}>Vencimento</th>}
      </tr></thead>
      <tbody>
        {dados.clientes.map((c, i) => (
          <tr key={i}>
            <td style={S.td}>{c.nome}</td>
            <td style={S.tdR}>{brl(c.valor_comissao)}</td>
            <td style={S.tdR}>{brl(c.despesas)}</td>
            <td style={S.tdR}>{brl(c.subtotal)}</td>
            {comVenc && (
              <td style={S.tdR}>
                {c.vencimento ? <>{fmtVenc(c.vencimento)}{c.prazo_dias != null && <div style={{ fontSize: 10, color: '#6b7280' }}>{rotulo(c.prazo_dias)}</div>}</> : ''}
              </td>
            )}
          </tr>
        ))}
        <tr style={S.totRow}>
          <td style={{ ...S.td, borderTop: '2px solid #f59e0b' }}><strong>TOTAL</strong></td>
          <td style={{ ...S.tdR, borderTop: '2px solid #f59e0b' }}><strong>{brl(totComissao)}</strong></td>
          <td style={{ ...S.tdR, borderTop: '2px solid #f59e0b' }}><strong>{brl(totDespesas)}</strong></td>
          <td style={{ ...S.tdR, borderTop: '2px solid #f59e0b' }}><strong>{brl(totSubtotal)}</strong></td>
          {comVenc && <td style={{ ...S.tdR, borderTop: '2px solid #f59e0b' }} />}
        </tr>
      </tbody>
    </table>
  )
}

// ─── Fluxo 3: Comissão Separada ──────────────────────────────────────────────
function TabelaFluxo3({ dados }) {
  const renderSecao = (titulo, clientes, colValor, colTotal) => {
    const totMacho = clientes.reduce((s, c) => s + c.macho, 0)
    const totFemea = clientes.reduce((s, c) => s + c.femea, 0)
    const totMisto = clientes.reduce((s, c) => s + c.misto, 0)
    const totQtd = clientes.reduce((s, c) => s + c.total, 0)
    const totValor = clientes.reduce((s, c) => s + c.valor, 0)
    const totComissao = clientes.reduce((s, c) => s + c.valor_comissao, 0)
    const totFinal = clientes.reduce((s, c) => s + c.total_final, 0)
    return (
      <>
        <div style={S.secTitle}>{titulo}</div>
        <table style={S.tbl}>
          <thead><tr>
            <th style={S.th}>Nome</th>
            <th style={S.thC}>Macho</th>
            <th style={S.thC}>Fêmea</th>
            <th style={S.thC}>Misto</th>
            <th style={S.thC}>Total</th>
            <th style={S.thR}>{colValor}</th>
            <th style={S.thR}>Valor Comissão</th>
            <th style={S.thR}>{colTotal}</th>
          </tr></thead>
          <tbody>
            {clientes.map((c, i) => (
              <tr key={i}>
                <td style={S.td}>
                  {c.nome}
                  {c.parcelamento_resumo && (
                    <div style={{ fontSize: 10, color: '#b45309', fontWeight: 600 }}>{c.parcelamento_resumo}</div>
                  )}
                </td>
                <td style={S.tdC}>{c.macho}</td>
                <td style={S.tdC}>{c.femea}</td>
                <td style={S.tdC}>{c.misto}</td>
                <td style={S.tdC}><strong>{c.total}</strong></td>
                <td style={S.tdR}>{brl(c.valor)}</td>
                <td style={S.tdR}>{brl(c.valor_comissao)}</td>
                <td style={S.tdR}>{brl(c.total_final)}</td>
              </tr>
            ))}
            <tr style={S.totRow}>
              <td style={{ ...S.td, borderTop: '2px solid #f59e0b' }}><strong>SUBTOTAL</strong></td>
              <td style={{ ...S.tdC, borderTop: '2px solid #f59e0b' }}><strong>{totMacho}</strong></td>
              <td style={{ ...S.tdC, borderTop: '2px solid #f59e0b' }}><strong>{totFemea}</strong></td>
              <td style={{ ...S.tdC, borderTop: '2px solid #f59e0b' }}><strong>{totMisto}</strong></td>
              <td style={{ ...S.tdC, borderTop: '2px solid #f59e0b' }}><strong>{totQtd}</strong></td>
              <td style={{ ...S.tdR, borderTop: '2px solid #f59e0b' }}><strong>{brl(totValor)}</strong></td>
              <td style={{ ...S.tdR, borderTop: '2px solid #f59e0b' }}><strong>{brl(totComissao)}</strong></td>
              <td style={{ ...S.tdR, borderTop: '2px solid #f59e0b' }}><strong>{brl(totFinal)}</strong></td>
            </tr>
          </tbody>
        </table>
      </>
    )
  }
  return (
    <>
      {dados.compradores && dados.compradores.length > 0 && renderSecao('Compradores', dados.compradores, 'Valor Compra', 'Total')}
      {dados.vendedores && dados.vendedores.length > 0 && renderSecao('Vendedores', dados.vendedores, 'Valor Venda', 'Total Líquido')}
    </>
  )
}

// ─── Componente principal ─────────────────────────────────────────────────────
export default function ComissoesCliente() {
  const { organizadorAtivo } = useOrganizador()
  const orgId = organizadorAtivo?.id
  const { user } = useAuthStore()
  const liberado = podeExportar(user)

  const [modoPeriodo, setModoPeriodo] = useState(false)
  const [dataInicio, setDataInicio] = useState(primeiroDiaMes())
  const [dataFim, setDataFim] = useState(hoje())
  const [erro, setErro] = useState('')
  const [loadingXlsx, setLoadingXlsx] = useState(false)
  const [leiloes, setLeiloes] = useState([])
  const [leilaoId, setLeilaoId] = useState('')
  const [dados, setDados] = useState(null)
  const [loading, setLoading] = useState(false)
  const [loadingL, setLoadingL] = useState(true)
  const [loadingPdf, setLoadingPdf] = useState(false)
  const [pdfUrl, setPdfUrl] = useState(null)
  const [showPdf, setShowPdf] = useState(false)
  const iframeRef = useRef(null)

  useEffect(() => {
    if (!orgId) return
    setLoadingL(true)
    leiloesService.getByOrganizador(orgId)
      .then(data => {
        const sorted = [...(data || [])].sort((a, b) => new Date(b.data_leilao) - new Date(a.data_leilao))
        setLeiloes(sorted)
        if (sorted.length) setLeilaoId(String((sorted.find(l => l.status === 'on') || sorted[0]).id))
      })
      .finally(() => setLoadingL(false))
  }, [orgId])

  const periodoAtivo = liberado && modoPeriodo

  useEffect(() => {
    if (!orgId) return
    if (periodoAtivo ? !(dataInicio && dataFim) : !leilaoId) { setDados(null); return }
    let ativo = true
    setLoading(true)
    setErro('')
    const req = periodoAtivo
      ? mapasService.getComissoesClientePeriodoDados(orgId, { dataInicio, dataFim })
      : mapasService.getComissoesClienteDados(orgId, leilaoId)
    req
      .then(res => { if (ativo) setDados(res) })
      .catch(err => { if (ativo) { setDados(null); setErro(periodoAtivo ? (err.response?.data?.detail || 'Nenhum leilão encontrado no período') : '') } })
      .finally(() => { if (ativo) setLoading(false) })
    return () => { ativo = false }
  }, [orgId, periodoAtivo, leilaoId, dataInicio, dataFim])

  const leilaoAtual = leiloes.find(l => String(l.id) === leilaoId)
  const periodoLabel = periodoAtivo
    ? `Período ${fmtDateBR(dataInicio)} a ${fmtDateBR(dataFim)}`
    : leilaoAtual ? `${leilaoAtual.nome} — ${fmtDate(leilaoAtual.data_leilao)}` : ''
  const logoOrgSrc = organizadorAtivo?.logo_url
    ? (organizadorAtivo.logo_url.startsWith('http') ? organizadorAtivo.logo_url
        : `${API_BASE}${organizadorAtivo.logo_url.startsWith('/') ? '' : '/'}${organizadorAtivo.logo_url}`)
    : null

  const handleImprimir = async () => {
    if (!orgId || (!periodoAtivo && !leilaoId)) return
    setLoadingPdf(true)
    try {
      const blob = periodoAtivo
        ? await mapasService.getComissoesClientePeriodoPdf(orgId, { dataInicio, dataFim })
        : await mapasService.getComissoesClientePdf(orgId, leilaoId)
      if (pdfUrl) URL.revokeObjectURL(pdfUrl)
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }))
      setPdfUrl(url)
      setShowPdf(true)
    } catch (err) {
      alert('Erro ao gerar PDF: ' + (err.response?.statusText || err.message))
    } finally { setLoadingPdf(false) }
  }
  const handleExportarXlsx = async () => {
    if (!orgId) return
    setLoadingXlsx(true)
    try {
      const blob = await mapasService.getComissoesClienteXlsx(orgId, periodoAtivo ? { dataInicio, dataFim } : { leilaoId })
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }))
      const a = document.createElement('a')
      a.href = url
      a.download = `comissoes_cliente_${periodoAtivo ? `${dataInicio}_a_${dataFim}` : (leilaoAtual?.nome || leilaoId)}.xlsx`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (err) {
      alert('Erro ao exportar planilha: ' + (err.response?.statusText || err.message))
    } finally { setLoadingXlsx(false) }
  }
  const closePdf = () => { setShowPdf(false); if (pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null) } }
  const printPdf = () => { if (iframeRef.current?.contentWindow) { iframeRef.current.contentWindow.focus(); iframeRef.current.contentWindow.print() } }

  if (!orgId) return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700">
      Selecione um organizador.
    </div>
  )

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <Users className="w-5 h-5 text-emerald-600" />
        <h1 className="text-lg font-semibold text-gray-800">Comissões por Cliente</h1>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div className="flex flex-wrap items-end gap-3">
          {liberado && (
            <div className="flex rounded-lg overflow-hidden border border-gray-200 h-[38px] self-end">
              <button onClick={() => setModoPeriodo(false)}
                className={`px-4 text-sm font-medium transition-colors ${!modoPeriodo ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                Por Leilão
              </button>
              <button onClick={() => setModoPeriodo(true)}
                className={`px-4 text-sm font-medium transition-colors ${modoPeriodo ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                Por Período
              </button>
            </div>
          )}
          {!periodoAtivo ? (
            <Select label="Leilão" value={leilaoId} onChange={setLeilaoId} disabled={loadingL} className="flex-[2] min-w-[200px]">
              {loadingL ? <option>Carregando...</option>
                : leiloes.map(l => <option key={l.id} value={String(l.id)}>{l.nome} — {fmtDate(l.data_leilao)}</option>)}
            </Select>
          ) : (
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500 font-medium">Período</label>
              <DateRangePicker
                startDate={dataInicio}
                endDate={dataFim}
                onChange={({ data_inicio, data_fim }) => { setDataInicio(data_inicio); setDataFim(data_fim) }}
                className="min-w-[220px]"
              />
            </div>
          )}
          <div className="flex items-end gap-2 ml-auto">
            {loading && <RefreshCw size={15} className="text-gray-400 animate-spin mb-2" />}
            {liberado && (
              <button onClick={handleExportarXlsx} disabled={!dados || loadingXlsx}
                className="flex items-center gap-2 px-4 py-2 h-9 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-200 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors">
                {loadingXlsx ? <RefreshCw size={15} className="animate-spin" /> : <Download size={15} />}
                Exportar Excel
              </button>
            )}
            <button onClick={handleImprimir} disabled={!dados || loadingPdf}
              className="flex items-center gap-2 px-4 py-2 h-9 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors">
              <Printer size={15} /> {loadingPdf ? 'Gerando...' : 'Imprimir'}
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : !dados ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400 text-sm">
          {erro || (periodoAtivo ? 'Selecione um período' : 'Selecione um leilão para visualizar')}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div style={S.page}>
            <div style={S.hdr}>
              <div style={{ minWidth: 140 }}>
                {logoOrgSrc ? <img src={logoOrgSrc} alt="logo" style={S.hdrLogo} /> : <span style={{ fontWeight: 700, fontSize: 14 }}>{organizadorAtivo?.name || organizadorAtivo?.nome || ''}</span>}
              </div>
              <div style={S.hdrCenter}>
                <div style={S.hdrT1}>COMISSÕES POR CLIENTE</div>
                <div style={S.hdrT2}>{organizadorAtivo?.name || organizadorAtivo?.nome || ''}</div>
                <div style={S.hdrT3}>{periodoLabel}</div>
              </div>
              <div style={{ minWidth: 140, textAlign: 'right' }}>
                <img src="/assets/rematei.png" alt="Rematei" style={S.hdrLogo} />
              </div>
            </div>

            {dados.fluxo === 'primeira_parcela' && <TabelaFluxo2 dados={dados} />}
            {dados.fluxo === 'gta_nota' && <TabelaFluxo2 dados={dados} />}
            {(dados.fluxo === 'separada' || dados.fluxo === 'cavalo') && <TabelaFluxo3 dados={dados} />}
            {dados.fluxo === 'periodo' && (
              <>
                {dados.clientes?.length > 0 && <TabelaFluxo2 dados={dados} />}
                {(dados.compradores?.length > 0 || dados.vendedores?.length > 0) && <TabelaFluxo3 dados={dados} />}
              </>
            )}

            <div style={S.footer}>www.rematei.com.br</div>
          </div>
        </div>
      )}

      {showPdf && pdfUrl && (
        <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-50 sm:p-4">
          <div className="bg-white w-full max-w-5xl flex flex-col rounded-t-2xl sm:rounded-xl shadow-2xl" style={{ height: '92dvh' }}>
            <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-2xl sm:rounded-t-xl flex-shrink-0">
              <span className="font-semibold text-gray-800 text-sm truncate flex-1 min-w-0">Comissões por Cliente — {periodoLabel}</span>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button onClick={() => downloadPdf(pdfUrl, `Comissões por Cliente - ${periodoLabel}.pdf`)}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium rounded-lg transition-colors">Salvar PDF</button>
                <button onClick={printPdf} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors">
                  <Printer size={15} /> Imprimir</button>
                <button onClick={closePdf} className="px-3 py-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm">Fechar</button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden rounded-b-xl">
              <iframe ref={iframeRef} src={pdfUrl} title="Comissões por Cliente" style={{ width: '100%', height: '100%', border: 'none' }} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
